/**
 * ScheduleMatchRow – einheitliche Match-Zeile für Lese- und Eintrags-Modus.
 * Wiederverwendbar: Spielplan, Spielplan-Übersicht, Entry, Finish, Clubmeisterschaft.
 *
 * layout="card" | "compact"
 *   - card: Karten-Layout (Court + Status, Teams, Ergebnis-Bereich)
 *   - compact: eine Zeile "Platz N: Team1 vs Team2  Ergebnis/Ausstehend"
 *
 * mode="read" | "entry"
 *   - read: Ergebnis/Status nur anzeigen
 *   - entry: Eingabefelder (card: children; compact: value/onChange oder children)
 */
import React from "react";
import { CircleAlert } from "lucide-react";

const COURT_ICON = "🎾";

export function ScheduleMatchRow({
  layout = "card",
  mode = "read",
  courtLabel,
  teamA,
  teamB,
  t,
  status = "pending",
  scoreText = "",
  errorMessage,
  value,
  onChange,
  children,
  team1LabelKey,
  team2LabelKey,
  showTeamLabels = false,
  matchTypeLabel,
  scoreLabelKey,
  statusPendingKey = "schedule.status.pending",
  statusEnteredKey = "schedule.status.entered",
  courtPrefixKey = "schedule.courtPrefix",
  vsKey = "common.vs",
  setLabelKey = "schleifchen.currentRound.setLabel",
  dataKey,
  className = "",
  ...rest
}) {
  const isCard = layout === "card";
  const isEntry = mode === "entry";
  const hasResult = status === "entered";
  const team1Label = team1LabelKey && t ? t(team1LabelKey, { n: "1" }) : "Team 1";
  const team2Label = team2LabelKey && t ? t(team2LabelKey) : "Team 2";
  const courtText = courtLabel != null && t ? t(courtPrefixKey, { n: String(courtLabel) }) : String(courtLabel ?? "");

  if (layout === "compact") {
    return (
      <div
        className={`tm-schedule-match tm-schedule-match--compact ${hasResult ? "tm-schedule-match--has-score" : ""} ${isEntry ? "tm-schedule-match--editing" : ""} ${className}`.trim()}
        data-key={dataKey}
        data-status={status}
        {...rest}
      >
        <span className="tm-schedule-match__court">{courtText}:</span>
        <span className="tm-schedule-match__teams">
          {teamA} {t ? t(vsKey) : "vs"} {teamB}
        </span>
        {isEntry ? (
          children ?? (
            <input
              type="text"
              className="tm-schedule-match__score-input"
              value={value ?? ""}
              onChange={(e) => onChange?.(e.target.value)}
              placeholder={t ? t(statusPendingKey) : ""}
              aria-label={courtText}
            />
          )
        ) : scoreText ? (
          <span className="tm-schedule-match__score">{scoreText}</span>
        ) : (
          <span className="tm-schedule-match__status muted">{t ? t(statusPendingKey) : "Ausstehend"}</span>
        )}
      </div>
    );
  }

  return (
    <article
      className={`tm-schedule-match tm-schedule-match--card ${hasResult ? "tm-schedule-match--entered" : ""} ${isEntry ? "tm-schedule-match--editing" : ""} ${className}`.trim()}
      data-key={dataKey}
      data-status={status}
      role="listitem"
      {...rest}
    >
      <div className="tm-schedule-match__top">
        <div className="tm-schedule-match__court">
          <span className="tm-schedule-match__icon" aria-hidden>{COURT_ICON}</span>
          <span>{courtText}</span>
          {matchTypeLabel ? <span className="tm-schedule-match__match-type muted">{matchTypeLabel}</span> : null}
        </div>
        <span
          className={`tm-schedule-match__status-badge muted ${hasResult ? "tm-schedule-match__status-badge--entered" : ""}`}
          aria-label={hasResult ? (t ? t(statusEnteredKey) : "") : (t ? t(statusPendingKey) : "")}
        >
          <CircleAlert className="tm-schedule-match__status-icon" aria-hidden />
          {hasResult ? (t ? t(statusEnteredKey) : "Eingetragen") : (t ? t(statusPendingKey) : "Ausstehend")}
        </span>
      </div>
      <div className="tm-schedule-match__body">
        <div className="tm-schedule-match__team tm-schedule-match__team--left">
          <span className="tm-schedule-match__team-name">{teamA}</span>
          {showTeamLabels ? <span className="muted tm-schedule-match__team-label">{team1Label}</span> : null}
        </div>
        <span className="tm-schedule-match__vs muted" aria-hidden>{t ? t(vsKey) : "vs"}</span>
        <div className="tm-schedule-match__right">
          <div className="tm-schedule-match__team tm-schedule-match__team--right">
            <span className="tm-schedule-match__team-name">{teamB}</span>
            {showTeamLabels ? <span className="muted tm-schedule-match__team-label">{team2Label}</span> : null}
          </div>
          <div className="tm-schedule-match__score-wrap">
            <span className="muted tm-schedule-match__score-label">{scoreLabelKey && t ? t(scoreLabelKey) : (t ? t(setLabelKey) : "Satz")}</span>
            {isEntry ? (
              <div className="tm-schedule-match__score-input-wrap">{children}</div>
            ) : scoreText ? (
              <span className="tm-schedule-match__score-value">{scoreText}</span>
            ) : (
              <span className="muted tm-schedule-match__score-placeholder">
                {t ? (t("schleifchen.currentRound.setPlaceholder") || "—") : "—"}
              </span>
            )}
          </div>
        </div>
      </div>
      {errorMessage ? (
        <div className="tm-schedule-match__error muted" role="alert">{errorMessage}</div>
      ) : null}
    </article>
  );
}
